import { useEffect, useState } from 'react';
import { composeQueryString } from '../../Query';
import { Query } from '../../../definitions/query';
import { QUERY_STRING } from '../../../enums/QueryString';
import { Referrers } from '../../../enums/Analytics';
import AuthService, {
  AuthProvider,
  OTTProvider,
} from '../../../Services/AuthService';
import useAppMode from '../config/useAppMode';
import useHistory from '../useBrowserHistory';

const useOttExchange = (): boolean => {
  const history = useHistory();
  const { isEmbeddedMode } = useAppMode({ strict: true });

  const [ottReady, setOttReady] = useState(false);

  useEffect(() => {
    handleOTT();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @warn this is duplicated here because the <Router> hasn't been initialized yet,
   *       so we can't use the `useQuery` hook to update the query string as it
   *       depends on the <Router> context
   * @see https://github.com/referenceable/referenceable_client/blob/develop/src/lib/hook/useQuery.ts
   */
  const updateQuery = (query: Query) => {
    history.replace({ search: composeQueryString(query) });
  };

  /**
   * Prepares the OTT value and its provider depending on the scenarios on
   * which a One Time Token could be given, such as SSO authentication, from
   * where the ott and provider come as query string params
   */
  const getOttData = (): [string | undefined, OTTProvider] => {
    const params = new URLSearchParams(window.location.search);

    let ott: string | undefined;
    let ottProvider: OTTProvider = AuthProvider.Unknown;

    const ottParam = params.get(QUERY_STRING.TOKEN);

    if (ottParam) {
      const providerParam = params.get(QUERY_STRING.PROVIDER) as OTTProvider;
      ottProvider = providerParam || AuthProvider.Unknown;
      ott = ottParam;
    }

    return [ott, ottProvider];
  };

  const handleOTT = async () => {
    const params = new URLSearchParams(window.location.search);
    const requestedNext = params.get(QUERY_STRING.NEXT);

    const query: Query = {};

    const [ott, ottProvider] = getOttData();
    if (!ott) {
      setOttReady(true);
      return;
    }

    console.log(`useOTTExchange:: Exchanging OTT from ${ottProvider}`);

    try {
      if (!isEmbeddedMode) {
        updateQuery({ [QUERY_STRING.SIGN_IN]: true });
      }

      const { nextPath: oAuthNext } = await AuthService.exchangeOTT(
        ott,
        ottProvider,
        Referrers.SIGN_IN.HOME
      );
      // The query string `next` has precedence over the oauth result
      query[QUERY_STRING.NEXT] = requestedNext || oAuthNext;
    } catch (error) {
      // Most likely rejected because the user is already logged in
      console.log(error);
      query[QUERY_STRING.SIGN_IN] = false;
      /**
       * For Mindtickle (vendor mode) this is specially important becase our
       * entrypoint always include an OTT even if the user is already logged in
       */
      query[QUERY_STRING.NEXT] = requestedNext;
    } finally {
      /**
       * When a `next` value is obtained from `exchangeOTT` or by query string
       * we need to resolve it, even if the user is not logged in. No need to
       * set the OTT as ready, as a page reload is going to occur. This
       * actually prevents unnecessary extra requests to be made.
       */
      setTimeout(() => {
        updateQuery(query);
        window.location.reload();
      }, 250);
    }
  };

  return ottReady;
};

export default useOttExchange;
